import { Component, OnInit } from '@angular/core';
import { FormArray, FormControl, FormGroup } from '@angular/forms';
import { DialogService, DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { MessageService } from 'primeng/api';
import { OverlayPanel } from 'primeng/overlaypanel';
import { firstValueFrom } from 'rxjs';
import { AssetManagementItem, DeliveryItem, DeliveryProduct } from 'src/app/api/models';
import { DeliveryService, WarehouseService } from 'src/app/api/services';
import { enumAssetManagementPurpose } from 'src/app/shared/enums/asset-management.enum';
import { ModalSerialInStockComponent } from 'src/app/shared/modal-serial-in-stock/modal-serial-in-stock.component';

interface SerialNumberGroup {
  code: FormControl<string>;
  asset_code: FormControl<string | undefined>;
  to_add: FormControl<boolean>;
}

interface ProductRowGroup {
  delivery_product_id: FormControl<number>;
  product_id: FormControl<number>;
  agreement_product_id: FormControl<number | undefined>;
  name: FormControl<string>;
  brand: FormControl<string | undefined>;
  code_internal: FormControl<string | undefined>;
  agreement_code: FormControl<string | undefined>;
  integration_code: FormControl<string | undefined>;
  required_asset_code: FormControl<boolean>;
  qty: FormControl<number>;
  tmp_serial: FormControl<string | undefined>;
  tmp_asset_code: FormControl<string | undefined>;
  tmp_serial_textarea: FormControl<string | undefined>;
  serial_numbers: FormArray<FormGroup<SerialNumberGroup>>;
}

@Component({
  selector: 'app-modal-manage-serials-staging',
  templateUrl: './modal-manage-serials-staging.component.html',
  styleUrls: ['./modal-manage-serials-staging.component.scss'],
})
export class ModalManageSerialsStagingComponent implements OnInit {
  delivery_id: number;
  delivery?: DeliveryItem;
  loading = false;
  saving = false;
  selectedItem?: FormGroup<ProductRowGroup>;

  items = new FormArray<FormGroup<ProductRowGroup>>([]);

  constructor(
    public ref: DynamicDialogRef,
    public config: DynamicDialogConfig,
    public dialogService: DialogService,
    private deliveryService: DeliveryService,
    private warehouseService: WarehouseService,
    private messageService: MessageService
  ) {
    this.delivery_id = config.data!.delivery_id;
  }

  ngOnInit() {
    this.load();
  }

  get selectedWarehouse() {
    return this.delivery?.related_warehouse;
  }

  load() {
    this.loading = true;
    this.deliveryService.deliveryControllerFindOne({ id: this.delivery_id }).subscribe({
      next: res => {
        this.delivery = res.data;
        this.items.clear();
        const stagingProducts = (res.data.delivery_products || []).filter(
          p => !!p.staging_activities
        );
        for (const p of stagingProducts) {
          this.items.push(this.buildProductGroup(p));
        }
        this.loading = false;
      },
      error: () => {
        this.loading = false;
      },
    });
  }

  private buildProductGroup(p: DeliveryProduct): FormGroup<ProductRowGroup> {
    return new FormGroup<ProductRowGroup>({
      delivery_product_id: new FormControl(p.id, { nonNullable: true }),
      product_id: new FormControl(p.product_id, { nonNullable: true }),
      agreement_product_id: new FormControl(p.agreement_product_id ?? undefined, {
        nonNullable: true,
      }),
      name: new FormControl(p.name, { nonNullable: true }),
      brand: new FormControl(p.brand ?? undefined, { nonNullable: true }),
      code_internal: new FormControl(p.code_internal ?? undefined, { nonNullable: true }),
      agreement_code: new FormControl(p.agreement_code ?? undefined, { nonNullable: true }),
      integration_code: new FormControl(p.integration_code ?? undefined, { nonNullable: true }),
      required_asset_code: new FormControl(!!p.agreement_product?.required_asset_code, {
        nonNullable: true,
      }),
      qty: new FormControl(p.qty, { nonNullable: true }),
      tmp_serial: new FormControl(undefined, { nonNullable: true }),
      tmp_asset_code: new FormControl(undefined, { nonNullable: true }),
      tmp_serial_textarea: new FormControl(undefined, { nonNullable: true }),
      serial_numbers: new FormArray<FormGroup<SerialNumberGroup>>(
        (p.asset_managements || []).map(
          am =>
            new FormGroup<SerialNumberGroup>({
              code: new FormControl(am.serial_number, { nonNullable: true }),
              asset_code: new FormControl(am.asset_code ?? undefined, { nonNullable: true }),
              to_add: new FormControl(false, { nonNullable: true }),
            })
        )
      ),
    });
  }

  getAddedSerials(group: FormGroup<ProductRowGroup>): string[] {
    return group.controls.serial_numbers.controls
      .filter(s => s.value.to_add === true)
      .map(s => s.value.code!);
  }

  goToAssetCode(event: KeyboardEvent, nextFocus: HTMLInputElement) {
    if (event.key === 'Enter' || event.key === 'Tab') {
      event.preventDefault();
      nextFocus.focus();
    }
  }

  addSerialKeyDown(
    event: KeyboardEvent,
    p: FormGroup<ProductRowGroup>,
    returnFocus?: HTMLInputElement
  ) {
    if (event.key === 'Enter' || event.key === 'Tab') {
      if (p.controls.qty.value > p.controls.serial_numbers.length) {
        event.preventDefault();
        this.addSerial(p);
        returnFocus?.focus();
      } else {
        this.messageService.add({
          severity: 'warn',
          summary: 'Impossibile serializzare',
          detail: 'Non puoi serializzare più asset della quantità prevista.',
        });
      }
    }
  }

  addSerial(p: FormGroup<ProductRowGroup>) {
    const isAssetCodeRequired = p.controls.required_asset_code.value;
    const serial = p.controls.tmp_serial.value;
    const assetCode = p.controls.tmp_asset_code.value;
    if (!serial) return;
    if (isAssetCodeRequired && !assetCode) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Asset code mancante',
        detail: "Per inserire l'asset code, scrivere [SERIAL_NUMBER]|[ASSET_CODE]",
      });
      return;
    }
    if (this.getAddedSerials(p).includes(serial)) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Serial number già presente',
        detail: 'Questo serial number è già stato aggiunto per questo prodotto.',
      });
      return;
    }
    this.warehouseService
      .warehouseControllerIsSerialInAssetManagement({
        product_id: p.controls.product_id.value,
        serialNo: serial,
      })
      .subscribe({
        next: r => {
          const currentWarehouseId = this.selectedWarehouse?.id;
          const relatedWarehouse = r.data.related_warehouse;
          const endCustomer = r.data.registry_end_customer;
          let detail: string;

          if (!relatedWarehouse) {
            detail = 'Serial number già esistente';
          } else if (currentWarehouseId === relatedWarehouse.id) {
            if (r.data.related_delivery_id !== this.delivery_id) {
              detail = `Questo serial number è presente nel magazzino ${relatedWarehouse.name}${
                endCustomer ? ' ' + endCustomer.name : ''
              }, ma è già impegnato in un'altra consegna.`;
            } else {
              p.controls.serial_numbers.push(
                new FormGroup<SerialNumberGroup>({
                  code: new FormControl(serial, { nonNullable: true }),
                  asset_code: new FormControl(assetCode ?? undefined, { nonNullable: true }),
                  to_add: new FormControl(false, { nonNullable: true }),
                })
              );
              p.controls.tmp_serial.setValue(undefined);
              p.controls.tmp_asset_code.setValue(undefined);
              return;
            }
          } else {
            detail = `Questo serial number è presente nel magazzino ${relatedWarehouse.name}${
              endCustomer ? ' ' + endCustomer.name : ''
            }.`;
          }

          if (r.message === 'serial number esistente ma inattivo') {
            detail = r.message;
          }

          this.messageService.add({ severity: 'warn', summary: 'Serial number già presente', detail });
        },
        error: () => {
          // Non esiste in nessun magazzino: posso aggiungerlo come nuovo
          p.controls.serial_numbers.push(
            new FormGroup<SerialNumberGroup>({
              code: new FormControl(serial, { nonNullable: true }),
              asset_code: new FormControl(assetCode ?? undefined, { nonNullable: true }),
              to_add: new FormControl(true, { nonNullable: true }),
            })
          );
          p.controls.tmp_serial.setValue(undefined);
          p.controls.tmp_asset_code.setValue(undefined);
        },
      });
  }

  selectSerial(p: FormGroup<ProductRowGroup>) {
    const selectedAssets = p.controls.serial_numbers.value
      .filter(s => !s.to_add)
      .map(s => s.code!);

    const ref = this.dialogService.open(ModalSerialInStockComponent, {
      data: {
        product_id: p.controls.product_id.value,
        warehouse_id: this.selectedWarehouse?.id,
        selection: true,
        selectedAssets,
        related_delivery_id: this.delivery_id,
        purpose: enumAssetManagementPurpose.VENDITA,
        related_agreeement_id: this.delivery?.agreement?.id,
      },
      header: `Serial number in ${this.selectedWarehouse?.name}`,
      modal: true,
      width: '70%',
    });
    ref.onClose.subscribe({
      next: (result: { selectedAssets: AssetManagementItem[] }) => {
        if (!result) return;
        const isAssetCodeRequired = p.controls.required_asset_code.value;
        const oldValues = p.controls.serial_numbers.value;
        p.controls.serial_numbers.clear();
        [
          ...oldValues.filter(ov => ov.to_add),
          ...result.selectedAssets
            .filter(sn => (isAssetCodeRequired ? !!sn.asset_code : true))
            .map(sn => ({ code: sn.serial_number, to_add: false, asset_code: sn.asset_code })),
        ].forEach(({ code, to_add, asset_code }) => {
          p.controls.serial_numbers.push(
            new FormGroup<SerialNumberGroup>({
              code: new FormControl(code!, { nonNullable: true }),
              asset_code: new FormControl(asset_code ?? undefined, { nonNullable: true }),
              to_add: new FormControl(!!to_add, { nonNullable: true }),
            })
          );
        });
        const missingAssetCode = result.selectedAssets.filter(sn =>
          isAssetCodeRequired ? !sn.asset_code : false
        );
        if (missingAssetCode.length > 0) {
          this.messageService.add({
            severity: 'warn',
            summary: 'Asset code mancanti',
            detail: `Gli asset con serial number ${missingAssetCode
              .map(sn => sn.serial_number)
              .join(', ')} non hanno l'asset code. Impossibile aggiungerli.`,
          });
        }
      },
    });
  }

  async addMultipleSerial(op: OverlayPanel) {
    const p = this.selectedItem!;
    const isAssetCodeRequired = p.controls.required_asset_code.value;
    const tmpSerialTextarea = p.controls.tmp_serial_textarea.value || '';
    const tmpSerials = tmpSerialTextarea.split(',');
    const serialAndAssetCods = tmpSerials.map((s: string) => s.split('|'));
    const serialsObjs = serialAndAssetCods.map(([serialNumber, assetCode]: string[]) => ({
      serialNumber: serialNumber?.trim(),
      assetCode: assetCode?.trim(),
    }));
    const serialObjsFiltered = serialsObjs.filter(({ serialNumber }) => !!serialNumber);
    const serials = new Map<string, { serialNumber: string; assetCode: string }>(
      serialObjsFiltered.map(s => [
        s.serialNumber.toUpperCase(),
        { serialNumber: s.serialNumber?.toUpperCase(), assetCode: s.assetCode?.toUpperCase() },
      ])
    );

    const added: string[] = [];
    const skipped: string[] = [];
    const notAdded: string[] = [];
    for (const serialKey of serials.keys()) {
      const { serialNumber: serialNo, assetCode } = serials.get(serialKey)!;
      if (!serialNo) {
        skipped.push(serialNo);
        continue;
      }
      if (!assetCode && isAssetCodeRequired) {
        skipped.push(serialNo);
        continue;
      }
      if (this.getAddedSerials(p).includes(serialNo)) {
        skipped.push(serialNo);
        continue;
      }
      const result = await firstValueFrom(
        this.warehouseService.warehouseControllerIsSerialInAssetManagement({
          product_id: p.controls.product_id.value,
          serialNo,
        })
      )
        .then(r => {
          if (
            r.data.related_warehouse &&
            this.selectedWarehouse &&
            r.data.related_warehouse.id === this.selectedWarehouse.id &&
            r.data.related_delivery_id === this.delivery_id
          ) {
            return serialNo;
          }
          notAdded.push(serialNo);
          return null;
        })
        .catch(() => serialNo);

      if (result) {
        p.controls.serial_numbers.push(
          new FormGroup<SerialNumberGroup>({
            code: new FormControl(result, { nonNullable: true }),
            asset_code: new FormControl(assetCode ?? undefined, { nonNullable: true }),
            to_add: new FormControl(true, { nonNullable: true }),
          })
        );
        added.push(serialNo);
      }
    }
    p.controls.tmp_serial_textarea.setValue('');
    this.selectedItem = undefined;
    this.messageService.clear();
    if (added.length > 0) {
      this.messageService.add({
        severity: 'success',
        summary: 'Serial numbers aggiunti',
        detail: 'Aggiunti: ' + added.join(', '),
        life: 10000,
      });
      op.hide();
    }
    if (notAdded.length > 0) {
      this.messageService.add({
        severity: 'error',
        summary: 'Serial numbers non aggiunti',
        detail: 'Non aggiunti: ' + notAdded.join(', '),
        life: 10000,
      });
    }
    if (skipped.length > 0) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Serial numbers saltati',
        detail: 'Saltati: ' + skipped.join(', '),
        life: 10000,
      });
    }
  }

  removeSerial(index: number, frmSerialNumbers: FormArray<FormGroup<SerialNumberGroup>>) {
    frmSerialNumbers.removeAt(index);
  }

  close() {
    this.ref.close();
  }

  save() {
    if (this.items.length === 0) {
      this.close();
      return;
    }
    this.saving = true;
    this.deliveryService
      .deliveryControllerSerializeProducts({
        id: this.delivery_id,
        body: {
          items: this.items.controls.map(p => ({
            delivery_product_id: p.controls.delivery_product_id.value,
            serial_numbers: p.controls.serial_numbers.value.map(sn => ({
              code: sn.code!,
              asset_code: sn.asset_code || undefined,
              to_add: !!sn.to_add,
            })),
          })),
        },
      })
      .subscribe({
        next: () => {
          this.saving = false;
          this.messageService.add({
            severity: 'success',
            summary: 'Seriali salvati',
            detail: 'I seriali sono stati salvati correttamente.',
          });
          this.ref.close({ updated: true });
        },
        error: err => {
          this.saving = false;
          this.messageService.add({
            severity: 'error',
            summary: 'Errore',
            detail: err.error?.message || 'Errore durante il salvataggio.',
          });
        },
      });
  }
}
